import type { PluginInstall } from '../core/kernel.js';

const FRAME_MS = 33;
const TAU = Math.PI * 2;

const R1 = 1;
const R2 = 2;
const K2 = 10;
const Y_SCALE = 0.5;

const THETA_STEP = 0.07;
const PHI_STEP = 0.02;
const A_STEP = 0.07;
const B_STEP = 0.03;

const RAMP = '.,-~:;=!*#$@';
const NBSP = String.fromCharCode(0xa0);
const SPACE_INDEX = 0xff;

const MIN_COLS = 30;
const MAX_COLS = 80;
const ROW_RATIO = 44 / 80;
const PROBE_COLS = 60;

type Geometry = { cols: number; rows: number; k1: number };

const geometryFor = (cols: number): Geometry => ({
  cols,
  rows: Math.round(cols * ROW_RATIO),
  k1: (cols * K2 * 3) / (8 * (R1 + R2)),
});

const measureCharWidth = (terminal: HTMLElement, font: string): number => {
  const probe = document.createElement('span');
  probe.style.position = 'absolute';
  probe.style.left = '-9999px';
  probe.style.top = '0';
  probe.style.whiteSpace = 'pre';
  probe.style.font = font;
  probe.textContent = NBSP.repeat(PROBE_COLS);
  document.body.append(probe);
  const width = probe.getBoundingClientRect().width / PROBE_COLS;
  probe.remove();
  return width;
};

const pickGeometry = (): Geometry => {
  const terminal = document.querySelector('.terminal') as HTMLElement | null;
  if (!terminal) {
    return geometryFor(MAX_COLS);
  }
  const style = getComputedStyle(terminal);
  const charWidth = measureCharWidth(terminal, style.font);
  if (!Number.isFinite(charWidth) || charWidth <= 0) {
    return geometryFor(MAX_COLS);
  }
  const padX =
    parseFloat(style.paddingLeft || '0') +
    parseFloat(style.paddingRight || '0');
  const innerWidth = terminal.clientWidth - padX;
  const fitCols = Math.floor(innerWidth / charWidth);
  return geometryFor(Math.max(MIN_COLS, Math.min(MAX_COLS, fitCols)));
};

const renderFrame = (
  geom: Geometry,
  charBuf: Uint8Array,
  zBuf: Float32Array,
  A: number,
  B: number
): void => {
  charBuf.fill(SPACE_INDEX);
  zBuf.fill(0);

  const cosA = Math.cos(A);
  const sinA = Math.sin(A);
  const cosB = Math.cos(B);
  const sinB = Math.sin(B);

  const { cols, rows, k1 } = geom;
  const cx = cols / 2;
  const cy = rows / 2;

  for (let theta = 0; theta < TAU; theta += THETA_STEP) {
    const cosTheta = Math.cos(theta);
    const sinTheta = Math.sin(theta);
    const circleX = R2 + R1 * cosTheta;
    const circleY = R1 * sinTheta;

    for (let phi = 0; phi < TAU; phi += PHI_STEP) {
      const cosPhi = Math.cos(phi);
      const sinPhi = Math.sin(phi);

      const x =
        circleX * (cosB * cosPhi + sinA * sinB * sinPhi) -
        circleY * cosA * sinB;
      const y =
        circleX * (sinB * cosPhi - sinA * cosB * sinPhi) +
        circleY * cosA * cosB;
      const z = K2 + cosA * circleX * sinPhi + circleY * sinA;
      const ooz = 1 / z;

      const xp = Math.floor(cx + k1 * ooz * x);
      const yp = Math.floor(cy - k1 * ooz * y * Y_SCALE);

      if (xp < 0 || xp >= cols || yp < 0 || yp >= rows) {
        continue;
      }

      const L =
        cosPhi * cosTheta * sinB -
        cosA * cosTheta * sinPhi -
        sinA * sinTheta +
        cosB * (cosA * sinTheta - cosTheta * sinA * sinPhi);

      if (L <= 0) {
        continue;
      }

      const idx = yp * cols + xp;
      if (ooz <= zBuf[idx]!) {
        continue;
      }
      zBuf[idx] = ooz;
      charBuf[idx] = Math.min(RAMP.length - 1, Math.floor(L * 8));
    }
  }
};

const renderRow = (
  cols: number,
  charBuf: Uint8Array,
  scratch: string[],
  row: number
): string => {
  for (let col = 0; col < cols; col++) {
    const v = charBuf[row * cols + col]!;
    scratch[col] = v === SPACE_INDEX ? NBSP : RAMP[v]!;
  }
  return scratch.join('');
};

const install: PluginInstall = kernel => {
  kernel.installExecutable('/bin/donut', {
    describe: 'spinning donut',
    async exec(ctx) {
      const geom = pickGeometry();
      const charBuf = new Uint8Array(geom.cols * geom.rows);
      const zBuf = new Float32Array(geom.cols * geom.rows);
      const scratch: string[] = new Array(geom.cols).fill(NBSP);

      let aborted = false;
      let armed = false;
      const onKeydown = () => {
        if (armed) {
          aborted = true;
        }
      };
      document.addEventListener('keydown', onKeydown);

      kernel.term.setBusy(true);
      kernel.term.setPromptVisible(false);

      const initial = NBSP.repeat(geom.cols);
      const lines: HTMLElement[] = [];
      for (let r = 0; r < geom.rows; r++) {
        lines.push(kernel.term.emitLine(initial, 'donut-line'));
      }

      try {
        let A = 0;
        let B = 0;
        while (!aborted) {
          renderFrame(geom, charBuf, zBuf, A, B);
          for (let r = 0; r < geom.rows; r++) {
            kernel.term.setLineContent(
              lines[r]!,
              renderRow(geom.cols, charBuf, scratch, r)
            );
          }
          A += A_STEP;
          B += B_STEP;
          await ctx.sleep(FRAME_MS);
          armed = true;
        }
      } finally {
        document.removeEventListener('keydown', onKeydown);
        for (const line of lines) {
          line.remove();
        }
        kernel.term.setBusy(false);
        kernel.term.setPromptVisible(true);
      }
      return 0;
    },
  });
};

export default install;
